/* Created by Interactive 63 - https://interactive63.com */
/**
 * Editing live text in the artwork: reading and writing a text element's content, font family,
 * size, weight and alignment, and adding new text layers. Like gradient stops, these change the
 * SVG source rather than adding animation, so text animates through the ordinary properties.
 *
 * Multi-line text is stored as one unnumbered <tspan> per non-empty line, with blank lines folded
 * into the next span's dy (an empty span would collapse and lose its break). Spans without a
 * data-st-id belong to their text element, so the editor can rewrite them freely; spans that were
 * numbered on import are layers in their own right and are never rewritten from the parent.
 */
import { parseColour } from '../colour';
import { elementIdFromIndex } from '../model/ids';
import type { ElementId, ElementMeta, Project } from '../model/types';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Gap between lines, relative to the font size. */
const LINE_HEIGHT_EM = 1.2;
/** New text is sized as this fraction of the artboard height. */
const NEW_TEXT_SIZE_RATIO = 0.125;
/** Moves the baseline down so a single line looks vertically centred on the insertion point. */
const BASELINE_CENTRE_EM = 0.35;
/** The CSS initial font size, which SVG text uses when nothing sets one. */
const DEFAULT_FONT_SIZE = 16;

export const TEXT_TAGS = new Set(['text', 'tspan']);
export const DEFAULT_TEXT_FONT = 'system-ui, sans-serif';
export const DEFAULT_TEXT_FILL = '#222222';
export const LIGHT_TEXT_FILL = '#ffffff';
/** Backgrounds darker than this perceived brightness (0 to 255) get light text. */
const DARK_BACKGROUND_BRIGHTNESS = 128;

export type TextAnchor = 'start' | 'middle' | 'end';

export interface TextStyle {
  fontFamily: string;
  fontSize: number;
  /** Numeric weight as a string, such as "400" or "700". */
  fontWeight: string;
  anchor: TextAnchor;
}

export interface TextInfo extends TextStyle {
  content: string;
  /** False when the element holds numbered spans, which must be edited one by one. */
  editable: boolean;
}

export interface NewTextOptions {
  content?: string;
  fontFamily?: string;
}

/** The attributes behind each style field, in the names SVG and CSS share. */
const STYLE_ATTRIBUTES: Record<keyof TextStyle, string> = {
  fontFamily: 'font-family',
  fontSize: 'font-size',
  fontWeight: 'font-weight',
  anchor: 'text-anchor',
};

const WEIGHT_KEYWORDS: Record<string, string> = { normal: '400', bold: '700' };

/** Parses the stored source into a document. */
function parse(source: string): Document {
  return new DOMParser().parseFromString(source, 'image/svg+xml');
}

/** Writes a document back into the project's source. */
function store(draft: Project, doc: Document): void {
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
}

/** Finds a text or tspan element by its element ID. */
function findText(doc: Document, id: ElementId): Element | null {
  const node = doc.querySelector(`[data-st-id="${id}"]`);
  return node && TEXT_TAGS.has(node.localName) ? node : null;
}

/** A presentation value set directly on one element, from its inline style or attribute. */
function ownValue(node: Element, name: string): string | null {
  const style = node.getAttribute('style') ?? '';
  const match = new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i').exec(style);
  const fromStyle = match?.[1]?.replace(/!important/i, '').trim();
  if (fromStyle) return fromStyle;
  return node.getAttribute(name);
}

/** A presentation value as the element inherits it, walking up to the root. */
function inheritedValue(node: Element, name: string): string | null {
  for (let at: Element | null = node; at; at = at.parentElement) {
    const value = ownValue(at, name);
    if (value !== null && value !== 'inherit') return value;
  }
  return null;
}

/** Removes one declaration from an inline style, since it would override the attribute. */
function dropStyleDeclaration(node: Element, name: string): void {
  const style = node.getAttribute('style');
  if (style === null) return;
  const rest = style
    .split(';')
    .filter((part) => part.split(':')[0]?.trim().toLowerCase() !== name)
    .join(';')
    .trim();
  if (rest === '') node.removeAttribute('style');
  else node.setAttribute('style', rest);
}

/** Whether the text holds spans that are layers of their own. */
export function hasNumberedChildren(node: Element): boolean {
  return node.querySelector('[data-st-id]') !== null;
}

/** The editor-owned line spans of a text element, or an empty list for plain text. */
function lineSpans(node: Element): Element[] {
  return Array.from(node.children).filter(
    (child) => child.localName === 'tspan' && !child.hasAttribute('data-st-id'),
  );
}

/** Reads a font size, resolving px and em against the inherited size. */
function parseFontSize(value: string | null, inherited: number): number {
  if (value === null) return inherited;
  const match = /^\s*([+-]?(?:\d+\.?\d*|\.\d+))\s*(px|em|%)?\s*$/i.exec(value);
  if (!match?.[1]) return inherited;
  const n = Number(match[1]);
  const unit = (match[2] ?? '').toLowerCase();
  if (unit === 'em') return n * inherited;
  if (unit === '%') return (n / 100) * inherited;
  return n;
}

/** The font size an element renders at, following em and percentage sizes up the tree. */
function resolvedFontSize(node: Element): number {
  const chain: Element[] = [];
  for (let at: Element | null = node; at; at = at.parentElement) chain.unshift(at);
  return chain.reduce(
    (size, at) => parseFontSize(ownValue(at, 'font-size'), size),
    DEFAULT_FONT_SIZE,
  );
}

/** Where lines start: the first x of the element or its nearest ancestor that sets one. */
export function lineStartX(node: Element): string {
  for (let at: Element | null = node; at; at = at.parentElement) {
    const x = at.getAttribute('x');
    if (x !== null) return x.trim().split(/[\s,]+/)[0] ?? '0';
  }
  return '0';
}

/** How many line breaks a line span's dy stands for. The first span may carry leading blank lines. */
function lineBreaksBefore(span: Element, index: number): number {
  const dy = parseFloat(span.getAttribute('dy') ?? '');
  const breaks = Number.isFinite(dy) ? Math.round(dy / LINE_HEIGHT_EM) : 0;
  return index === 0 ? Math.max(0, breaks) : Math.max(1, breaks);
}

/** The words of a text element, with editor-owned line spans read back as separate lines. */
export function textContentOf(node: Element): string {
  const lines = lineSpans(node);
  if (lines.length === 0) return node.textContent;
  return lines
    .map((line, index) => '\n'.repeat(lineBreaksBefore(line, index)) + line.textContent)
    .join('');
}

/**
 * Replaces an element's children with the given words. One line is plain text; several become
 * one span per non-empty line, each starting at x. Trailing blank lines are not kept.
 */
export function writeTextContent(target: Element, content: string, x: string): void {
  const doc = target.ownerDocument;
  const lines = content.replace(/\r\n?/g, '\n').split('\n');
  target.replaceChildren();
  if (lines.length === 1) {
    target.append(doc.createTextNode(lines[0] ?? ''));
    return;
  }
  let breaks = 0;
  lines.forEach((line, index) => {
    if (index > 0) breaks += 1;
    if (line === '') return;
    const span = doc.createElementNS(SVG_NS, 'tspan');
    span.setAttribute('x', x);
    if (breaks > 0) span.setAttribute('dy', `${Math.round(breaks * LINE_HEIGHT_EM * 100) / 100}em`);
    span.textContent = line;
    target.append(span);
    breaks = 0;
  });
}

/** Reads a text element's content and style, or null when it is not in the artwork. */
export function readText(source: string, id: ElementId): TextInfo | null {
  const node = findText(parse(source), id);
  if (!node) return null;
  const weight = (inheritedValue(node, 'font-weight') ?? '400').toLowerCase();
  const anchor = inheritedValue(node, 'text-anchor');
  const editable = !hasNumberedChildren(node);
  return {
    content: textContentOf(node),
    editable,
    fontFamily: inheritedValue(node, 'font-family') ?? '',
    fontSize: Math.round(resolvedFontSize(node) * 100) / 100,
    fontWeight: WEIGHT_KEYWORDS[weight] ?? weight,
    anchor: anchor === 'middle' || anchor === 'end' ? anchor : 'start',
  };
}

/**
 * Replaces a text element's content, as plain text or line spans. Returns false, changing
 * nothing, when the element is missing or holds numbered spans.
 */
export function setTextContent(draft: Project, id: ElementId, content: string): boolean {
  const doc = parse(draft.svg.source);
  const node = findText(doc, id);
  if (!node || hasNumberedChildren(node)) return false;
  writeTextContent(node, content, lineStartX(node));
  store(draft, doc);
  return true;
}

/**
 * Writes font and alignment settings onto a text element as attributes, clearing any inline
 * declaration that would override them. Returns whether the element was found.
 */
export function setTextStyle(draft: Project, id: ElementId, patch: Partial<TextStyle>): boolean {
  const doc = parse(draft.svg.source);
  const node = findText(doc, id);
  if (!node) return false;
  for (const key of Object.keys(patch) as (keyof TextStyle)[]) {
    const value = patch[key];
    if (value === undefined) continue;
    if (key === 'fontSize' && !(typeof value === 'number' && value > 0)) continue;
    const attribute = STYLE_ATTRIBUTES[key];
    const text = String(value).trim();
    if (text === '') node.removeAttribute(attribute);
    else node.setAttribute(attribute, text);
    dropStyleDeclaration(node, attribute);
  }
  store(draft, doc);
  return true;
}

/** A fill for new text that stands out from the artboard colour, dark when there is none. */
export function newTextFill(background: string | undefined): string {
  const colour = background === undefined ? null : parseColour(background);
  if (!colour || colour.a < 0.5) return DEFAULT_TEXT_FILL;
  const brightness = 0.299 * colour.r + 0.587 * colour.g + 0.114 * colour.b;
  return brightness < DARK_BACKGROUND_BRIGHTNESS ? LIGHT_TEXT_FILL : DEFAULT_TEXT_FILL;
}

/** The highest element number in use. */
function highestIndex(elements: Record<ElementId, ElementMeta>): number {
  return Object.keys(elements).reduce((top, id) => Math.max(top, Number(id.slice(1))), 0);
}

/**
 * Adds a text layer at the top of the stack, centred in the artboard and sized to suit it.
 * Returns the new element's ID.
 */
export function addTextLayer(draft: Project, options: NewTextOptions = {}): ElementId {
  const doc = parse(draft.svg.source);
  const id = elementIdFromIndex(highestIndex(draft.elements) + 1);
  const { x, y, width, height } = draft.svg.viewBox;
  const size = Math.max(1, Math.round(height * NEW_TEXT_SIZE_RATIO * 10) / 10);
  const round = (n: number): string => String(Math.round(n * 100) / 100);

  const node = doc.createElementNS(SVG_NS, 'text');
  node.setAttribute('data-st-id', id);
  node.setAttribute('x', round(x + width / 2));
  node.setAttribute('y', round(y + height / 2 + size * BASELINE_CENTRE_EM));
  node.setAttribute('font-family', options.fontFamily ?? DEFAULT_TEXT_FONT);
  node.setAttribute('font-size', String(size));
  node.setAttribute('font-weight', '400');
  node.setAttribute('text-anchor', 'middle');
  node.setAttribute('fill', newTextFill(draft.svg.background));
  node.textContent = options.content ?? 'Text';
  doc.documentElement.append(node);
  store(draft, doc);

  draft.elements[id] = {
    id,
    tag: 'text',
    label: 'Text',
    parentId: null,
    childIds: [],
    origin: { x: 50, y: 50 },
    excludeFromExport: false,
    hiddenInEditor: false,
  };
  draft.rootIds.push(id);
  return id;
}
